import Configuration from '../constant/configuration';
import { getToken, isOwnTokenWrite } from './tokenStorage';
import { normalizeServingSizes } from '../utils/cconfigCompat';
import { recordBackendFromResponse } from './deviceBackend';
import { clearRateLimit, isRateLimited, noteRateLimited, rateLimitRemainingMs } from './rateLimitBackoff';
import errorLogger from '../utils/errorLogger';
import { isTabHidden } from '../utils/tabVisibility';
import { AppConstant } from '../constant/AppConstant';
const axios = require('axios').default;

export default class GlobalAccess {
  // Track pending requests to prevent race conditions
  static pendingDevicesRequest = null;
  static devicesRequestId = 0;
  static isDevicesFetching = false;
  static fetchingWatchdog = null;

  // Track pending servings requests to prevent race conditions
  static pendingServingsRequest = null;
  static servingsRequestId = 0;

  // OPTIMIZATION: Debounce timer for progress updates
  static progressUpdateTimeout = null;

  // Per-request timeouts (ms). Long enough for slow cellular, short enough that the
  // static fetch flag cannot deadlock subsequent retries when the radio drops.
  static CUSTOMER_TIMEOUT_MS = 15000;
  static DEVICE_TIMEOUT_MS = 12000;
  // Max device fetches in flight at once. Same rationale as SERVINGS_CONCURRENCY
  // below: axios's timeout clock starts at request CREATION, so firing all ~100
  // device GETs at once makes everything past the ~6-connection browser cap burn
  // its 12 s budget queued and time out — dropping live devices from that poll.
  // Bounding concurrency keeps the timeout measuring real network time. This is
  // the primary driver of the large-fleet keg-list flicker.
  static DEVICE_CONCURRENCY = 6;
  // A pubkey that fails to resolve for this many CONSECUTIVE polls is treated as a
  // dead, rotated-out key — mDash leaves the old key on the customer record after a
  // Wi-Fi credential change until a backend cron prunes it — and skipped on the
  // next fan-out. This stops the per-cycle 404 / net::ERR_FAILED console spam and
  // frees connection slots for live devices. Any successful resolve clears the
  // counter; a brand-new key from /customer always starts fresh. The threshold is
  // high enough that a few transient timeouts of a LIVE device never quarantine it.
  static DEAD_KEY_THRESHOLD = 4;
  // Consecutive per-pubkey resolution-failure counts (see DEAD_KEY_THRESHOLD).
  static deviceFailCounts = {};
  // Probation probes for quarantined keys. Quarantine must not be permanent: a
  // device that comes back online under the SAME pubkey (e.g. plain power/network
  // outage, not a key rotation) would otherwise never reappear for the life of
  // the SPA session, since a skipped key can never resolve and reset its counter.
  // Each quarantined key is re-attempted on a per-key wall-clock schedule with
  // exponential backoff — first probe PROBATION_BASE_DELAY_MS after quarantine,
  // doubling per failed probe up to PROBATION_MAX_DELAY_MS — so a truly-dead key
  // costs at most one request every few minutes (instead of one per 3-30 s poll),
  // while a recovered device is picked up within minutes, resets its counter via
  // the normal success path, and rejoins every fan-out.
  static PROBATION_BASE_DELAY_MS = 30000;
  static PROBATION_MAX_DELAY_MS = 300000;
  // Max probation probes admitted to any single fan-out. A fleet-wide Wi-Fi
  // credential change rotates many pubkeys at once, so their probes all come due
  // together; without a cap they would fill the DEVICE_CONCURRENCY pool and make
  // LIVE devices wait behind requests that are expected to fail. Over-quota keys
  // stay due and are picked up by the next poll — probed keys back off, so the
  // queue drains fairly rather than starving the tail.
  static PROBATION_MAX_PROBES_PER_POLL = 2;
  // Per-quarantined-pubkey probe schedule: pubkey → { delayMs, nextProbeAt }.
  // Entries are created when a key enters quarantine and removed when the key
  // resolves or leaves the customer record.
  static deviceProbeSchedule = {};
  // Debounce window for progressive device rendering. As devices resolve during
  // the bounded fan-out we paint them in batches rather than waiting for the whole
  // pool — so a cold load (where dead keys squat connection slots for the full
  // timeout) fills the table in as data arrives instead of showing a long blank.
  static DEVICE_RENDER_DEBOUNCE_MS = 120;
  // Servings queries are deliberately more generous: query cost scales with
  // serving volume, so the busiest (most business-relevant) devices have the
  // slowest queries — a tight timeout would systematically drop exactly the
  // taps that matter most. A timed-out device gets one silent retry, then is
  // reported via app.state.servingsMissingDevices so the UI can flag
  // incomplete totals instead of silently under-reporting.
  static SERVINGS_TIMEOUT_MS = 30000;
  // Max servings requests in flight at once. The per-request timeout clock
  // starts when axios CREATES the request, not when the browser dispatches it,
  // so firing all N at once makes the requests beyond the ~6-connection limit
  // burn their timeout budget queued — wide date ranges then "all time out"
  // even though each query would succeed if given a real connection. Bounding
  // concurrency keeps the timeout measuring actual network time. Set near the
  // HTTP/1.1 per-host cap so we rarely queue at the browser level.
  static SERVINGS_CONCURRENCY = 6;
  // Servings request body. `kegs: true` asks the compat layer to add the
  // device's keg cost history (supaDash devices only) so reports price each
  // keg at the cost entered for it; mdash.net ignores the field.
  static servingsBody = (startDate, endDate) => ({ params: [startDate, endDate], kegs: true });
  // Window of the most recent getAllServings call, kept so the partial-data
  // banner's "retry missing" action can refetch ONLY the failed devices.
  static _lastServingsWindow = null;
  // Pubkeys already attempted for _lastServingsWindow. Bounds backfillServings
  // so a device that keeps failing can't be re-requested on every render.
  static _servingsAttempted = new Set();
  // True once a device fan-out has run to completion at least once. This is NOT
  // the same as app.state.devicesLoaded, which the progressive paint flips true
  // while the fan-out is still in flight — so it cannot tell "this key is gone"
  // from "this key hasn't resolved yet". activePubkeys needs that distinction.
  static devicesSettledOnce = false;

  /**
   * Run `worker(item)` over `items` with at most `concurrency` in flight at a
   * time. Resolves when all settle. Never rejects — a worker that throws
   * `CANCELLED` aborts the remaining queue and the returned promise rejects
   * with that error so callers can treat the whole load as cancelled.
   */
  static _runServingsPool = (items, concurrency, worker) => {
    return new Promise((resolve, reject) => {
      if (items.length === 0) { resolve(); return; }
      let next = 0;
      let active = 0;
      let finished = 0;
      let aborted = false;

      const pump = () => {
        if (aborted) return;
        while (active < concurrency && next < items.length) {
          const item = items[next++];
          active++;
          Promise.resolve()
            .then(() => worker(item))
            .then(() => {
              active--; finished++;
              if (finished === items.length) resolve();
              else pump();
            })
            .catch((err) => {
              // worker is expected to swallow normal failures itself; a throw
              // here means cancellation — stop the whole pool.
              if (!aborted) { aborted = true; reject(err); }
            });
        }
      };
      pump();
    });
  };
  // Watchdog upper bound — guarantees isDevicesFetching always self-heals.
  static FETCH_WATCHDOG_MS = 30000;

  /**
   * Split the customer's pubkeys into the set to fetch this poll. Live keys
   * (below DEAD_KEY_THRESHOLD) are always fetched. Quarantined keys are fetched
   * only when their probation probe is due, and at most
   * PROBATION_MAX_PROBES_PER_POLL of them per cycle; those are also returned in
   * `probedKeys` so the post-fan-out bookkeeping can back off failed probes.
   */
  static _selectFetchKeys = (keys, nowMs) => {
    const fetchKeys = [];
    const probedKeys = [];
    keys.forEach((k) => {
      if ((GlobalAccess.deviceFailCounts[k] || 0) < GlobalAccess.DEAD_KEY_THRESHOLD) {
        fetchKeys.push(k);
        return;
      }
      const schedule = GlobalAccess.deviceProbeSchedule[k];
      if (!schedule) {
        // Key just crossed the threshold — start its probation clock; the first
        // probe fires PROBATION_BASE_DELAY_MS from now.
        GlobalAccess.deviceProbeSchedule[k] = {
          delayMs: GlobalAccess.PROBATION_BASE_DELAY_MS,
          nextProbeAt: nowMs + GlobalAccess.PROBATION_BASE_DELAY_MS
        };
        return;
      }
      if (nowMs >= schedule.nextProbeAt &&
          probedKeys.length < GlobalAccess.PROBATION_MAX_PROBES_PER_POLL) {
        fetchKeys.push(k);
        probedKeys.push(k);
      }
    });
    return { fetchKeys, probedKeys };
  };

  static _clearFetchingFlag = () => {
    GlobalAccess.isDevicesFetching = false;
    if (GlobalAccess.fetchingWatchdog) {
      clearTimeout(GlobalAccess.fetchingWatchdog);
      GlobalAccess.fetchingWatchdog = null;
    }
  };

  // Completion callbacks waiting on the in-flight devices fetch. A callback
  // handed to getDevices while the mutex is held used to be dropped on the
  // floor, so Settings' Untappd-token load and Global Config's post-save
  // verify waited on a completion that never came. Every call now queues its
  // callback and the fetch that completes (success OR failure) drains the
  // queue — each callback runs exactly once, with the freshest state.
  static _deviceCallbacks = [];

  static _fireDeviceCallbacks = (untappdToken) => {
    // Snapshot first: a callback may call getDevices again and queue more.
    const callbacks = GlobalAccess._deviceCallbacks.splice(0);
    callbacks.forEach((cb) => {
      try {
        cb(untappdToken);
      } catch (err) {
        errorLogger.logError('DevicesFetchCallbackFailed', err);
      }
    });
  };

  // Allow consumers (e.g. route components on unmount) to abort an in-flight devices
  // fetch so its result can't land on a now-stale view.
  static cancelPendingDevicesRequest = () => {
    // The caller is abandoning the result (route unmount), so nobody should
    // hear about it either; a later page's fetch starts with a clean queue.
    GlobalAccess._deviceCallbacks = [];
    if (GlobalAccess.pendingDevicesRequest) {
      try {
        GlobalAccess.pendingDevicesRequest.cancel('Caller cancelled');
      } catch {
        // ignore
      }
      GlobalAccess.pendingDevicesRequest = null;
      // Retire the cancelled run's id so its pool drain can never become the
      // authoritative pass: the per-device cancels are swallowed and the pool
      // still drains, and before this bump a drain with a matching id scored
      // every unfetched key as a failure — four quick navigations away from a
      // Site page quarantined the tail of the fleet (audit 2026-10-15, H3).
      GlobalAccess.devicesRequestId++;
    }
    GlobalAccess._clearFetchingFlag();
  };

  // Abort an in-flight servings load (full fan-out, backfill or retry) and
  // retire its request id, so neither its data nor its loading/error state can
  // land after the caller has moved on (sign-out — see resetSessionState).
  static cancelPendingServingsRequest = () => {
    if (GlobalAccess.pendingServingsRequest) {
      try {
        GlobalAccess.pendingServingsRequest.cancel('Caller cancelled');
      } catch {
        // ignore
      }
      GlobalAccess.pendingServingsRequest = null;
    }
    GlobalAccess.servingsRequestId++;
    clearTimeout(GlobalAccess.progressUpdateTimeout);
    GlobalAccess.progressUpdateTimeout = null;
  };

  // Same for the cleaning-alerts fan-out.
  static _cancelPendingAlertsRequest = () => {
    if (GlobalAccess.pendingAlertsRequest) {
      try {
        GlobalAccess.pendingAlertsRequest.cancel('Caller cancelled');
      } catch {
        // ignore
      }
      GlobalAccess.pendingAlertsRequest = null;
    }
    GlobalAccess.alertsRequestId++;
  };

  /**
   * Sign-out (services/sessionReset.js). Every field here is class-level, so
   * it outlives route changes — and, before this existed, sign-out: the next
   * account in the same tab inherited the previous one's quarantine, settled
   * flag, servings window and queued callbacks, and an in-flight fetch could
   * land the previous account's devices (audit 2026-10-15, M5).
   *
   * Cancels and RETIRES every in-flight request (the request ids are bumped
   * even when nothing is pending, so a handler that is already past its
   * cancel token still bails), releases the fetching mutex and its watchdog,
   * and returns all per-session state to its initial value. This is NOT for
   * transient errors — those must keep prior data (CLAUDE.md, "Device Data
   * Loading & Resilience").
   */
  static resetSessionState = () => {
    GlobalAccess._clearFrameRefetches();
    GlobalAccess._clearRateLimitRetry();
    clearRateLimit(); // the limit is per token
    GlobalAccess.resetMembershipRefresh();
    GlobalAccess.cancelPendingDevicesRequest();
    GlobalAccess.devicesRequestId++;
    GlobalAccess.cancelPendingServingsRequest();
    GlobalAccess._cancelPendingAlertsRequest();
    GlobalAccess._clearFetchingFlag();
    GlobalAccess._deviceCallbacks = [];
    GlobalAccess.deviceFailCounts = {};
    GlobalAccess.deviceProbeSchedule = {};
    GlobalAccess.devicesSettledOnce = false;
    GlobalAccess._lastServingsWindow = null;
    GlobalAccess._servingsAttempted = new Set();
  };

  /**
   * Push-driven update for ONE device (2026-10-31).
   *
   * A notify frame is `{id, name: "updated", data}` where `data` is the full
   * GET /api/v2/m/device body (the compat layer builds both from the same
   * deviceView; mDash's frames carry the device too). Until this existed the
   * Site page answered every frame with TWO full account fan-outs — for a
   * 14-device account, 30 requests per pour — and ignored the frame's body.
   *
   * Apply the body when it looks like a device; otherwise (a frame without a
   * usable body) fetch just that one device, coalesced per pubkey so a burst
   * of frames costs one request. Never a full fan-out: the Site page's slow
   * reconciliation poll and the pageshow/visibility refetches cover membership
   * changes.
   */
  static FRAME_REFETCH_COALESCE_MS = 250;
  static _frameRefetchTimers = {};
  // Bumped by resetSessionState: a single-device fetch that started under the
  // previous account must not land in the next one's state.
  static _sessionGeneration = 0;

  static applyDeviceUpdate = (app, pubkey, msg) => {
    if (!app || !pubkey) return;
    if (!GlobalAccess.isPubkeyListed(app.state, pubkey)) return; // a frame for a device this account no longer lists (sign-out/switch)
    const data = msg && msg.data;
    if (data && data.shadow && data.shadow.state && data.shadow.state.reported) {
      GlobalAccess._storeDevice(app, pubkey, data);
      return;
    }
    GlobalAccess._scheduleDeviceRefresh(app, pubkey);
  };

  /** True when `state` (app.state or a setState updater's prevState) lists this device. */
  static isPubkeyListed = (state, pubkey) =>
    Boolean(state && ((state.pubkeys && pubkey in state.pubkeys) || (state.devices && pubkey in state.devices)));

  /**
   * One decision for every account-socket frame, shared by the Sites list and
   * the Site page (2026-10-31). Returns what it did so tests can pin it:
   *  - `membership`  the account's device list changed (insert/delete/org
   *                  move/backend change) → coalesced full refetch
   *  - `applied`     a data frame for a device we list → applyDeviceUpdate
   *  - `unknown`     a data frame for a device we do NOT list → our list is
   *                  stale, so the same coalesced refetch. The frame is never
   *                  written into state (applyDeviceUpdate's security
   *                  property): the refetch is what introduces the device.
   *  - `ignored`     anything else (pong, garbage, a frame naming no device)
   */
  static handleAccountFrame = (app, msg) => {
    if (!app || !msg || typeof msg !== 'object') return 'ignored';
    if (msg.name === 'membership') {
      GlobalAccess.requestMembershipRefresh(app);
      return 'membership';
    }
    const pubkey = GlobalAccess.resolveFramePubkey(app, msg);
    if (pubkey) {
      if (GlobalAccess.isPubkeyListed(app.state, pubkey)) {
        GlobalAccess.applyDeviceUpdate(app, pubkey, msg);
        return 'applied';
      }
      GlobalAccess.requestMembershipRefresh(app);
      return 'unknown';
    }
    // No pubkey and no id that matches a listed device. A frame that still
    // names a device id is about a device we do not list; one that names
    // nothing is not a device frame at all.
    const hasId = msg.id !== undefined && msg.id !== null && msg.id !== '';
    if (hasId) {
      GlobalAccess.requestMembershipRefresh(app);
      return 'unknown';
    }
    return 'ignored';
  };

  /**
   * Coalesced, rate-limited full getDevices(app) for membership changes
   * (2026-10-31). Before push, a device added to or removed from the account
   * showed on OTHER open dashboards within 10-30 s; with push the only thing
   * left was the 5-minute reconcile. The account socket's `membership` frame
   * (and a frame for a device we do not list) now asks for a refetch here:
   *  - at most one every MEMBERSHIP_REFRESH_MIN_MS (15 s);
   *  - a call inside that window, or while a fetch is already in flight,
   *    schedules exactly ONE trailing refresh and later calls fold into it, so
   *    a burst of frames (an org move touches many devices) costs two
   *    fan-outs at most;
   *  - the trailing refresh waits (re-arming, bounded by the fetch watchdog)
   *    if a fetch is still in flight when it fires.
   * Cleared by resetSessionState so a refresh never lands in the next account.
   */
  static MEMBERSHIP_REFRESH_MIN_MS = 15000;
  static MEMBERSHIP_BUSY_RETRY_MS = 2000;
  static _membershipLastAt = null;
  static _membershipTimer = null;

  static requestMembershipRefresh = (app) => {
    if (!app || GlobalAccess._membershipTimer) return; // a trailing refresh is already booked
    const now = Date.now();
    const windowEnd = GlobalAccess._membershipLastAt === null
      ? now
      : GlobalAccess._membershipLastAt + GlobalAccess.MEMBERSHIP_REFRESH_MIN_MS;
    if (windowEnd <= now && !GlobalAccess.isDevicesFetching) {
      GlobalAccess._runMembershipRefresh(app);
      return;
    }
    const floorMs = GlobalAccess.isDevicesFetching ? GlobalAccess.MEMBERSHIP_BUSY_RETRY_MS : 0;
    GlobalAccess._armMembershipTimer(app, Math.max(windowEnd - now, floorMs));
  };

  static _armMembershipTimer = (app, delayMs) => {
    GlobalAccess._membershipTimer = setTimeout(() => {
      GlobalAccess._membershipTimer = null;
      if (GlobalAccess.isDevicesFetching) {
        GlobalAccess._armMembershipTimer(app, GlobalAccess.MEMBERSHIP_BUSY_RETRY_MS);
        return;
      }
      GlobalAccess._runMembershipRefresh(app);
    }, delayMs);
  };

  static _runMembershipRefresh = (app) => {
    GlobalAccess._membershipLastAt = Date.now();
    GlobalAccess.getDevices(app);
  };

  /** Forget the rate-limit window and drop any booked trailing refresh (sign-out, account switch, tests). */
  static resetMembershipRefresh = () => {
    if (GlobalAccess._membershipTimer) clearTimeout(GlobalAccess._membershipTimer);
    GlobalAccess._membershipTimer = null;
    GlobalAccess._membershipLastAt = null;
  };

  /**
   * Pubkey for an account-socket frame (2026-10-31). Frames carry `pubkey`;
   * when one does not (an older compat build), match the frame's device `id`
   * against the devices this account already lists. Returns null for anything
   * unknown: a device newly added to the account is introduced by a
   * membership refetch (handleAccountFrame), never by trusting a frame to
   * introduce a key.
   */
  static resolveFramePubkey = (app, msg) => {
    if (!app || !msg) return null;
    if (msg.pubkey) return msg.pubkey;
    if (msg.id === undefined || msg.id === null || msg.id === '') return null;
    const devices = app.state.devices || {};
    const wanted = String(msg.id);
    const found = Object.keys(devices).find((k) => {
      const d = devices[k];
      return d && d.id !== undefined && d.id !== null && String(d.id) === wanted;
    });
    return found || null;
  };

  static _scheduleDeviceRefresh = (app, pubkey) => {
    // Both of these end in a full refresh, which covers this device.
    if (isTabHidden()) return;
    if (isRateLimited()) {
      GlobalAccess._scheduleRateLimitRetry(app);
      return;
    }
    if (GlobalAccess._frameRefetchTimers[pubkey]) return;
    GlobalAccess._frameRefetchTimers[pubkey] = setTimeout(() => {
      delete GlobalAccess._frameRefetchTimers[pubkey];
      GlobalAccess.refreshDevice(app, pubkey);
    }, GlobalAccess.FRAME_REFETCH_COALESCE_MS);
  };

  /** GET one device and merge it into app.state.devices. Errors are logged, never surfaced: the next frame or reconciliation poll retries. */
  static refreshDevice = (app, pubkey) => {
    const generation = GlobalAccess._sessionGeneration;
    const url = `${Configuration.mdashURL}/api/v2/m/device?access_token=${encodeURIComponent(pubkey)}`;
    return axios({ url, timeout: GlobalAccess.DEVICE_TIMEOUT_MS })
      .then((res) => {
        if (generation !== GlobalAccess._sessionGeneration) return; // signed out / switched meanwhile
        recordBackendFromResponse(pubkey, res);
        GlobalAccess._storeDevice(app, pubkey, res.data);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (GlobalAccess._noteIfRateLimited(err)) {
          GlobalAccess._scheduleRateLimitRetry(app);
          return;
        }
        errorLogger.logError('DeviceRefreshFailed', err);
      });
  };

  /** Sanitize and merge one device row; additive, never removes others. */
  static _storeDevice = (app, pubkey, data) => {
    // Deep copy: _sanitizeDeviceConfig edits nested config in place, and a
    // socket frame's body must not be mutated under the caller.
    const device = JSON.parse(JSON.stringify(data));
    GlobalAccess._sanitizeDeviceConfig(device);
    app.setState((prevState) => {
      // Membership is re-checked at WRITE time: between the frame and this
      // updater the tab may have signed out or switched accounts, and a device
      // the current account does not list must never be written into it.
      if (!GlobalAccess.isPubkeyListed(prevState, pubkey)) return null;
      GlobalAccess.deviceFailCounts[pubkey] = 0;
      return {
        devices: { ...(prevState.devices || {}), [pubkey]: device },
        devicesLoaded: true,
        loadError: null,
      };
    });
  };

  /** Drop pending single-device refetches (sign-out / account switch). */
  static _clearFrameRefetches = () => {
    Object.values(GlobalAccess._frameRefetchTimers).forEach((t) => clearTimeout(t));
    GlobalAccess._frameRefetchTimers = {};
    GlobalAccess._sessionGeneration++;
  };

  /**
   * User-initiated Retry (the error + Retry screens on Sites / Site / Settings).
   * A whole-fan-out failure scores EVERY key, so a short outage on a 3 s poll
   * quarantines the whole fleet; without this reset each Retry tap would
   * return the same error instantly — no request sent — until the first
   * probation probe up to PROBATION_MAX_DELAY_MS later. A tap on Retry is an
   * explicit "try everything again", so the quarantine is cleared first.
   */
  static retryDevices = (app, callback) => {
    GlobalAccess.deviceFailCounts = {};
    GlobalAccess.deviceProbeSchedule = {};
    GlobalAccess._clearRateLimitRetry();
    clearRateLimit();
    app.setState({ loadError: null });
    GlobalAccess.getDevices(app, callback);
  };

  // Normalize one device record IN PLACE so a malformed shadow can't poison
  // downstream rendering/calculations: coerce invalid bizHour*Start time fields
  // to "00:00" and replace any NaN numbers with 0. Run per-device as each fetch
  // resolves so progressively-rendered data is already clean.
  static _sanitizeDeviceConfig = (device) => {
    const config = device?.shadow?.state?.reported?.config;
    if (!config) return;
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    days.forEach((day) => {
      const startField = `bizHour${day}Start`;
      if (config[startField] !== undefined && config[startField] !== null) {
        if (typeof config[startField] !== 'string' || !config[startField].match(/^\d{1,2}:\d{2}$/)) {
          config[startField] = "00:00";
        }
      }
    });
    const checkForNaN = (obj) => {
      if (obj && typeof obj === 'object') {
        Object.keys(obj).forEach((key) => {
          const value = obj[key];
          if (typeof value === 'number' && isNaN(value)) {
            obj[key] = 0;
          } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            checkForNaN(value);
          }
        });
      }
    };
    checkForNaN(config);
  };

  // Registered by App via services/sessionSync.js (same wiring as the auth
  // interceptor's setSessionLostHandler — GlobalAccess cannot import the
  // module that resets it). Reconciles this tab with a token another tab
  // stored; returns true when the session changed.
  static sessionChangeHandler = null;

  static setSessionChangeHandler = (handler) => {
    GlobalAccess.sessionChangeHandler = typeof handler === 'function' ? handler : null;
  };

  /**
   * True when the stored token is not the one this tab's state was built
   * from and this tab did not write it: another tab signed in or out, and the
   * storage event was missed. Fetching now would merge the other account's
   * devices into this account's state (production 2026-10-16).
   */
  static _sessionChangedElsewhere = (app, token) => {
    const tabToken = app && app.state ? app.state.ktok : null;
    return !!tabToken && token !== tabToken && !isOwnTokenWrite(token);
  };

  /**
   * Gate for every account-data fetch: when the session changed elsewhere,
   * hand the switch to the session-sync handler (which resets, adopts and
   * refetches) and report true so the caller does not fetch.
   */
  static _blockedBySessionChange = (app, token) => {
    if (!GlobalAccess._sessionChangedElsewhere(app, token)) return false;
    if (GlobalAccess.sessionChangeHandler) GlobalAccess.sessionChangeHandler(app);
    return true;
  };

  /** Record a 429 (the interceptor's toast aside). True when `err` was one. */
  static _noteIfRateLimited = (err) => {
    if (err?.response?.status !== 429) return false;
    noteRateLimited(err.response);
    return true;
  };

  // One refresh when the current back-off ends, so a page with no poll of its
  // own (Site) recovers without the user doing anything.
  static _rateLimitRetryTimer = null;

  static _scheduleRateLimitRetry = (app) => {
    if (GlobalAccess._rateLimitRetryTimer) return;
    GlobalAccess._rateLimitRetryTimer = setTimeout(() => {
      GlobalAccess._rateLimitRetryTimer = null;
      GlobalAccess.getDevices(app);
    }, rateLimitRemainingMs());
  };

  static _clearRateLimitRetry = () => {
    clearTimeout(GlobalAccess._rateLimitRetryTimer);
    GlobalAccess._rateLimitRetryTimer = null;
  };

  static getDevices = (app, callback) => {
    const token = getToken();
    if (GlobalAccess._blockedBySessionChange(app, token)) return;
    // Defensive check: don't make API calls if not authenticated
    if (!token) {
      return;
    }

    // A background tab does not fetch: every tab of a browser profile shares the
    // token and its 30 requests/min. services/tabForeground.js refreshes on the
    // tab's return. A callback means a page is waiting on an answer, so it runs.
    if (!callback && isTabHidden()) {
      return;
    }

    // During a back-off nothing is requested. A waiting callback stays queued
    // and is answered by the retry when the back-off ends.
    if (isRateLimited()) {
      if (callback) GlobalAccess._deviceCallbacks.push(callback);
      GlobalAccess._scheduleRateLimitRetry(app);
      return;
    }

    // Skip if request already in progress to prevent cancellation cascades —
    // but never lose the caller's callback: it runs when that fetch completes.
    if (GlobalAccess.isDevicesFetching) {
      if (callback) GlobalAccess._deviceCallbacks.push(callback);
      return;
    }
    if (callback) GlobalAccess._deviceCallbacks.push(callback);

    // Mark as fetching, with a watchdog so a hung promise can never deadlock retries.
    GlobalAccess.isDevicesFetching = true;
    if (GlobalAccess.fetchingWatchdog) clearTimeout(GlobalAccess.fetchingWatchdog);
    GlobalAccess.fetchingWatchdog = setTimeout(() => {
      // Force-clear the gate; in-flight axios calls will time out on their own.
      GlobalAccess.isDevicesFetching = false;
      GlobalAccess.fetchingWatchdog = null;
    }, GlobalAccess.FETCH_WATCHDOG_MS);

    // Cancel any pending request
    if (GlobalAccess.pendingDevicesRequest) {
      GlobalAccess.pendingDevicesRequest.cancel('New request started');
    }

    // Create new cancel token for this request
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    GlobalAccess.pendingDevicesRequest = source;

    // Increment request ID to track this specific request
    const currentRequestId = ++GlobalAccess.devicesRequestId;
    axios.get(`${Configuration.mdashURL  }/customer?access_token=${  encodeURIComponent(token)}`, {
      cancelToken: source.token,
      timeout: GlobalAccess.CUSTOMER_TIMEOUT_MS
    }).then((res) => {
      // Ignore if this isn't the latest request
      if (currentRequestId !== GlobalAccess.devicesRequestId) {
        return;
      }
      let devices = {},
        pubkeys = res.data.pubkeys || {},
        isDemo = res.data.cconfig?.isDemo || 0,
        untappd_token = res.data.cconfig?.untappd_token || "";

      // Publish "the customer record is known" — and with it isDemo — now,
      // not after the device fan-out. Pages wait on customerLoaded before
      // asking the billing worker anything; at boot isDemo is still its
      // default 0, so the public demo account used to 403 on every page.
      if (!app.state.customerLoaded || app.state.isDemo !== isDemo) {
        app.setState({ isDemo, customerLoaded: true });
      }

      // Ensure bizDayStart is a valid time string, default to "00:00"
      let bizDayStart = res.data.cconfig?.bizDayStart;
      if (!bizDayStart || typeof bizDayStart !== 'string' || !bizDayStart.match(/^\d{1,2}:\d{2}$/)) {
        bizDayStart = "00:00";
      }

      // Load enabled serving sizes configuration, default to Configuration defaults.
      // normalizeServingSizes handles both compact (v2) and legacy formats.
      let enabledServingSizes = normalizeServingSizes(res.data.cconfig?.enabledServingSizes);
      if (!enabledServingSizes) {
        enabledServingSizes = Configuration.defaultEnabledServingSizes;
      }

      let keys = Object.keys(pubkeys);
      // Skip keys quarantined as dead (rotated-out) — see DEAD_KEY_THRESHOLD —
      // except the ones whose probation probe is due, so a device that recovers
      // under the same pubkey can rejoin the fan-out.
      const { fetchKeys, probedKeys } = GlobalAccess._selectFetchKeys(keys, Date.now());

      // Progressive rendering: paint devices as they resolve instead of waiting
      // for the whole bounded fan-out to finish. On a large fleet — or a cold load
      // where the dead keys squat connection slots for the full timeout — this
      // replaces a multi-second blank/spinner with a table that fills in as data
      // arrives. Debounced so ~100 resolutions don't trigger ~100 re-renders, and
      // gated on currentRequestId so a superseded fetch never paints.
      let renderTimer = null;
      let pendingRender = false;
      const flushIncrementalRender = () => {
        renderTimer = null;
        if (!pendingRender || currentRequestId !== GlobalAccess.devicesRequestId) return;
        pendingRender = false;
        app.setState((prevState) => {
          const prev = prevState.devices || {};
          let changed = false;
          const next = { ...prev };
          Object.keys(devices).forEach((k) => {
            if (next[k] !== devices[k]) { next[k] = devices[k]; changed = true; }
          });
          if (!changed) return null;
          // Additive only: first data clears the loading spinner, and a slow/dead
          // key still in flight can never remove an already-painted device. The
          // authoritative setState after the pool reconciles membership/loadError.
          return { devices: next, devicesLoaded: true, loadError: null };
        });
      };
      const scheduleIncrementalRender = () => {
        pendingRender = true;
        if (renderTimer === null) {
          renderTimer = setTimeout(flushIncrementalRender, GlobalAccess.DEVICE_RENDER_DEBOUNCE_MS);
        }
      };

      // Keys whose GET was cancelled or rate limited rather than failed. Neither
      // carries any signal about the device, so the authoritative pass must not
      // score them: a burst of 429s would otherwise quarantine live devices.
      const unscoredKeys = new Set();
      let sawRateLimit = false;
      const fetchDevice = (k) => {
        let url = `${Configuration.mdashURL  }/api/v2/m/device?access_token=${  encodeURIComponent(k)}`;
        return axios({
          method: 'GET',
          url,
          cancelToken: source.token,
          timeout: GlobalAccess.DEVICE_TIMEOUT_MS
        })
          .then((res) => {
            // Learn which backend serves this device from the compat layer's
            // `X-Kegtron-Backend` response header, so websocketService can open
            // the notify socket directly against mdash.net for mDash-backed
            // devices instead of paying for a compat Durable Object relay.
            // Header absent (older compat build, cached SW response) → the
            // backend stays unknown and the socket falls back to API_ORIGIN.
            recordBackendFromResponse(k, res);
            const data = res.data;
            GlobalAccess._sanitizeDeviceConfig(data);
            devices[k] = data;
            scheduleIncrementalRender();
          })
          .catch((err) => {
            // Continue loading other devices even if one fails. This includes a
            // 401/403 for an unknown/rotated device key: the auth interceptor
            // ignores device-key 401s, so they land here and are scored by
            // deviceFailCounts (quarantine) like any other unresolved key.
            if (axios.isCancel(err)) {
              unscoredKeys.add(k);
            } else if (GlobalAccess._noteIfRateLimited(err)) {
              unscoredKeys.add(k);
              sawRateLimit = true;
            }
          });
      };
      // Bound concurrency so queued requests don't burn their timeout budget
      // waiting for a connection (the servings path does the same). The worker
      // swallows its own errors, so the pool never rejects here.
      GlobalAccess._runServingsPool(fetchKeys, GlobalAccess.DEVICE_CONCURRENCY, fetchDevice).then(() => {
        // The authoritative pass below supersedes any pending incremental paint.
        if (renderTimer !== null) { clearTimeout(renderTimer); renderTimer = null; }

        // Double-check this is still the latest request before updating state.
        // A superseded run owns nothing any more: the fetching flag belongs to
        // the newer request (clearing it here let overlapping callers cancel
        // each other for as long as a fan-out outlasted the poll interval, so
        // the authoritative pass never ran — audit 2026-10-15, H3).
        if (currentRequestId !== GlobalAccess.devicesRequestId) {
          return;
        }

        // A full fan-out has now completed, so "absent from devices" carries
        // real signal. See devicesSettledOnce / activePubkeys.
        GlobalAccess.devicesSettledOnce = true;

        const fetchedAny = Object.keys(devices).length > 0;

        // Track consecutive per-pubkey resolution failures so dead (rotated-out)
        // keys quarantine themselves out of future fan-outs. Only keys we actually
        // ATTEMPTED this cycle are scored: a key that resolved resets to 0 and
        // leaves probation entirely (a recovered device rejoins every fan-out); a
        // key that failed increments. Quarantined keys whose probe wasn't due are
        // left untouched — incrementing them would inflate the counter forever for
        // no signal. A failed probe backs its next attempt off exponentially, so a
        // permanently-dead key settles at one request per PROBATION_MAX_DELAY_MS
        // rather than one per poll.
        const probedSet = new Set(probedKeys);
        const settledAt = Date.now();
        fetchKeys.forEach((k) => {
          // A cancelled or rate-limited request is neither a success nor a
          // failure — leave the key's counter and schedule untouched.
          if (unscoredKeys.has(k)) return;
          if (devices[k]) {
            GlobalAccess.deviceFailCounts[k] = 0;
            delete GlobalAccess.deviceProbeSchedule[k];
            return;
          }
          GlobalAccess.deviceFailCounts[k] = (GlobalAccess.deviceFailCounts[k] || 0) + 1;
          const schedule = GlobalAccess.deviceProbeSchedule[k];
          if (!probedSet.has(k) || !schedule) return;
          const nextDelay = Math.min(schedule.delayMs * 2, GlobalAccess.PROBATION_MAX_DELAY_MS);
          GlobalAccess.deviceProbeSchedule[k] = {
            delayMs: nextDelay,
            nextProbeAt: settledAt + nextDelay
          };
        });
        // Forget keys no longer on the customer record.
        Object.keys(GlobalAccess.deviceFailCounts).forEach((k) => {
          if (!(k in pubkeys)) delete GlobalAccess.deviceFailCounts[k];
        });
        Object.keys(GlobalAccess.deviceProbeSchedule).forEach((k) => {
          if (!(k in pubkeys)) delete GlobalAccess.deviceProbeSchedule[k];
        });

        // Merge by current pubkey membership: prefer the freshly-fetched record,
        // else retain the last-known one. A device that merely timed out THIS poll
        // (routine on a large fleet behind the ~6-connection cap) keeps its prior
        // data instead of vanishing — the root cause of the keg-list flicker.
        // Keys no longer present in the customer record are dropped. (Never wipe
        // devices on transient errors — see CLAUDE.md resilience invariants.)
        const buildMerged = (prev) => {
          const merged = {};
          keys.forEach((k) => {
            if (devices[k]) merged[k] = devices[k];
            else if (prev[k]) merged[k] = prev[k];
          });
          return merged;
        };

        // PERF: when a poll yields data identical to what's already in state,
        // skip the setState entirely — replacing `devices` with a new object
        // re-renders every consumer (whole tree, every 3-10 s poll) even when
        // nothing changed. Compare the MERGED result (what we'd actually store)
        // against CURRENT state so retained-on-timeout devices register as "no
        // change" and don't trigger a needless re-render. Self-correcting: if
        // anything mutated devices locally the snapshots differ and the fresh
        // copy still lands.
        let nothingChanged = false;
        if (fetchedAny && app.state.devicesLoaded === true && !app.state.loadError) {
          try {
            const nextSnapshot = JSON.stringify([
              buildMerged(app.state.devices || {}), pubkeys, isDemo,
              untappd_token, bizDayStart, enabledServingSizes,
            ]);
            const prevSnapshot = JSON.stringify([
              app.state.devices, app.state.pubkeys, app.state.isDemo,
              app.state.untappd_token, app.state.bizDayStart, app.state.enabledServingSizes,
            ]);
            nothingChanged = nextSnapshot === prevSnapshot;
          } catch (e) {
            // Circular or non-serializable state — fall through to setState.
            nothingChanged = false;
          }
        }

        // Release the mutex BEFORE the callbacks run, so a callback that
        // starts a follow-up fetch (Global Config's post-save verify) gets a
        // real fetch instead of being queued behind a flag nobody will clear.
        if (GlobalAccess.pendingDevicesRequest === source) {
          GlobalAccess.pendingDevicesRequest = null;
        }
        GlobalAccess._clearFetchingFlag();

        if (sawRateLimit) GlobalAccess._scheduleRateLimitRetry(app);

        if (nothingChanged) {
          GlobalAccess._fireDeviceCallbacks(untappd_token);
        } else {
        app.setState((prevState) => {
          const prevDevices = prevState.devices || {};
          const hadPrev = Object.keys(prevDevices).length > 0;
          const mergedDevices = buildMerged(prevDevices);
          const hasAnyMerged = Object.keys(mergedDevices).length > 0;
          // Fall back to prior data only if the merge produced nothing at all
          // (zero current pubkeys AND nothing retained) — otherwise the merge IS
          // the freshest complete view.
          const nextDevices = hasAnyMerged ? mergedDevices : (hadPrev ? prevDevices : devices);
          // Error only when the WHOLE fan-out failed. With data to show it is a
          // soft error (the UI prompts retry without flipping to a spinner);
          // with NOTHING to show — a first load where /customer succeeded but
          // every device GET failed (suspended org, compat-layer outage,
          // cellular timeout burst) — devicesLoaded must stay false so Sites /
          // Site / Settings render their error + Retry state instead of the
          // genuine "No devices" empty state (audit 2026-10-15, H1). Zero
          // pubkeys on /customer is still the genuine empty state.
          // Partial failures are invisible: the missing devices are retained.
          const wholeFanoutFailed = keys.length > 0 && !fetchedAny;
          const firstLoadFailed = wholeFanoutFailed && !hadPrev && !hasAnyMerged;
          return {
            devices: nextDevices,
            devicesLoaded: !firstLoadFailed,
            pubkeys: pubkeys && Object.keys(pubkeys).length > 0
              ? pubkeys
              : (prevState.pubkeys || pubkeys),
            isDemo,
            untappd_token,
            bizDayStart,
            enabledServingSizes,
            loadError: wholeFanoutFailed ? 'ERROR_LOADING_DEVICES' : null
          };
        }, () => {
          GlobalAccess._fireDeviceCallbacks(untappd_token);
        });
        }
      }).catch(() => {
        // Clear fetching flag if anything in the fan-out handler throws — but
        // only while this request still owns it — and release anyone waiting
        // on this fetch. A superseded request leaves both to the newer one.
        if (currentRequestId === GlobalAccess.devicesRequestId) {
          GlobalAccess._clearFetchingFlag();
          GlobalAccess._fireDeviceCallbacks(app.state.untappd_token);
        }
      });
    }).catch((e) => {
      // A superseded or cancelled request owns neither the fetching flag nor
      // loadError any more: the newer request (or the caller that cancelled)
      // already took over both. Clearing the flag here used to release the
      // newer request's mutex mid-flight (audit 2026-10-15, H3).
      if (currentRequestId !== GlobalAccess.devicesRequestId) {
        return;
      }

      // Clear fetching flag on error
      GlobalAccess._clearFetchingFlag();

      // Ignore if request was cancelled
      if (axios.isCancel(e)) {
        return;
      }

      if (GlobalAccess._noteIfRateLimited(e)) GlobalAccess._scheduleRateLimitRetry(app);

      // Session-token auth errors (401/403 on /customer) are handled by the axios
      // interceptor; per-device-key 401s never reach here. For transient network
      // errors do NOT wipe previously-loaded devices/pubkeys — that turns a flaky
      // cellular blip into a stuck loading spinner on every page that reads devices.
      // Surface the error via loadError so the UI can render an inline message + retry.
      app.setState((prevState) => {
        const hadDevices = prevState.devices && Object.keys(prevState.devices).length > 0;
        return {
          // Preserve existing data if we have any; only fall through to {} on first load.
          devices: hadDevices ? prevState.devices : (prevState.devices || {}),
          pubkeys: hadDevices ? prevState.pubkeys : (prevState.pubkeys || {}),
          // devicesLoaded stays whatever it was; on first-ever failure it remains false
          // so Site renders an error state rather than the indefinite spinner.
          loadError: e.response ? 'ERROR_LOADING_DEVICES' : 'ERROR_NETWORK'
        };
      }, () => {
        // A page waiting on this fetch (Settings Retry, Global Config verify)
        // must hear about the failure too, or it spins forever.
        GlobalAccess._fireDeviceCallbacks(app.state.untappd_token);
      });
    });
  };

  /**
   * Filter a list of pubkeys down to "active" devices, dropping stale keys.
   *
   * A Pro device rotates its pubkey when its Wi-Fi credentials change; the old
   * key lingers on the customer record until a backend cron prunes it. While it
   * lingers, the device API returns 401 for it. mdash.net's 401 carries no CORS
   * header (the browser reports a generic network error); the compat layer's
   * 401 IS readable, but either way the status is not used here — the per-key
   * fetch in getDevices swallows every failure, and services/authInterceptor.js
   * only logs out on a 401 for the SESSION token, never for a device key. The
   * reliable signal is that a stale key never resolves to a device in
   * getDevices, so it's absent from app.state.devices while still present in
   * pubkeys. Treat "in pubkeys but not in devices" as stale and exclude it, so
   * it never drives a "didn't load" banner or a Retry that can never succeed.
   *
   * Falls back to returning all keys when we cannot yet classify staleness:
   *  - no devices have resolved at all (e.g. a deep-link straight to /reports
   *    before the devices fetch lands), or
   *  - no device fan-out has COMPLETED yet (devicesSettledOnce === false).
   *
   * The second case is not academic. The fan-out is bounded to
   * DEVICE_CONCURRENCY and paints progressively, so for the first seconds of a
   * load app.state.devices holds an arbitrary SUBSET of live devices — and
   * app.state.devicesLoaded is already true (the incremental paint sets it).
   * Filtering against that subset silently dropped whole sites from the
   * servings fan-out: those devices were never requested, so they never even
   * reached servingsMissingDevices and no partial-data banner appeared. The
   * report just rendered without them until the user reloaded. Never treat
   * "hasn't resolved yet" as "rotated out".
   */
  static activePubkeys = (app, keys) => {
    const devices = (app.state && app.state.devices) || {};
    if (Object.keys(devices).length === 0 || !GlobalAccess.devicesSettledOnce) {
      return keys;
    }
    return keys.filter((k) => devices[k]);
  };

  static getAllServings = (app, startDate, endDate) => {
    const token = getToken();
    if (GlobalAccess._blockedBySessionChange(app, token)) return;
    if (!token) {
      return;
    }

    // Cancel any pending servings request
    if (GlobalAccess.pendingServingsRequest) {
      GlobalAccess.pendingServingsRequest.cancel('New servings request started');
    }

    // Create new cancel token for this request
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    GlobalAccess.pendingServingsRequest = source;

    // Increment request ID to track this specific request
    const currentRequestId = ++GlobalAccess.servingsRequestId;

    GlobalAccess._lastServingsWindow = { startDate, endDate };

    // Keep the previous servings in state during the refetch — the Reports UI
    // renders them dimmed under an updating overlay instead of flashing back
    // to a blank loading screen (stale-while-revalidate). The fresh data
    // replaces them atomically in the final setState below.
    app.setState({
      isServingsLoading: true,
      servingsLoadingProgress: { current: 0, total: 0 },
      servingsMissingDevices: [],
      servingsRetryAttempted: false,
      servingsError: null
    });

    // Reuse the pubkeys getDevices already loaded instead of re-fetching
    // /customer (a redundant serial round-trip before the fan-out could even
    // start). Fall back to /customer only when state has none (deep link
    // straight to /reports before the devices fetch lands).
    const statePubkeys = app.state.pubkeys || {};
    const pubkeysPromise = Object.keys(statePubkeys).length > 0
      ? Promise.resolve(statePubkeys)
      : axios.get(`${Configuration.mdashURL  }/customer?access_token=${  encodeURIComponent(token)}`, {
        cancelToken: source.token,
        timeout: GlobalAccess.CUSTOMER_TIMEOUT_MS
      }).then((res) => res.data.pubkeys || {});

    pubkeysPromise.then((pubkeys) => {
      // Ignore if this isn't the latest request
      if (currentRequestId !== GlobalAccess.servingsRequestId) {
        return;
      }
      let servings = {};

      // Only fetch servings for devices that actually resolved — stale (rotated-out)
      // pubkeys are skipped so they never fail every fetch and trip the partial-data
      // banner. See activePubkeys for the WiFi-rotation rationale.
      let keys = GlobalAccess.activePubkeys(app, Object.keys(pubkeys));
      // Reset the attempted-set for this window so backfillServings can tell a
      // device we already requested from one that resolved after this fan-out.
      GlobalAccess._servingsAttempted = new Set(keys);
      const totalDevices = keys.length;
      let completedDevices = 0;

      // Update initial progress
      app.setState({
        servingsLoadingProgress: { current: 0, total: totalDevices }
      });

      // OPTIMIZATION: Debounced progress update function
      const updateProgress = () => {
        clearTimeout(GlobalAccess.progressUpdateTimeout);
        GlobalAccess.progressUpdateTimeout = setTimeout(() => {
          app.setState({
            servingsLoadingProgress: { current: completedDevices, total: totalDevices }
          });
        }, 150); // 150ms debounce for smooth updates without render thrashing
      };

      // One device's servings fetch. The timeout bounds the worst case: a
      // single hung request used to gate the ENTIRE report indefinitely via
      // Promise.all. Failed devices get one silent retry round below; devices
      // that still fail are surfaced via servingsMissingDevices.
      const fetchOne = (k) => axios({
        method: 'POST',
        url: `${Configuration.mdashURL  }/api/v2/m/device/data/servings-device?access_token=${  encodeURIComponent(k)}`,
        data: GlobalAccess.servingsBody(startDate, endDate),
        cancelToken: source.token,
        timeout: GlobalAccess.SERVINGS_TIMEOUT_MS
      }).then((res) => {
        servings[k] = res.data;
      });

      // Pooled worker: fetch one device, counting progress at its first-round
      // outcome (success OR failure) so the bar advances steadily instead of
      // freezing at 0 while a wave of slow requests runs. A cancellation is
      // re-thrown to abort the whole pool.
      const firstRoundFailed = [];
      const firstRoundWorker = (k) =>
        fetchOne(k)
          .catch((err) => {
            if (axios.isCancel(err)) throw err;
            firstRoundFailed.push(k);
          })
          .then(() => {
            completedDevices++;
            // OPTIMIZATION: Debounced progress update to reduce re-renders
            updateProgress();
          });

      return GlobalAccess._runServingsPool(keys, GlobalAccess.SERVINGS_CONCURRENCY, firstRoundWorker).then(() => {
        if (currentRequestId !== GlobalAccess.servingsRequestId) {
          return undefined;
        }
        if (firstRoundFailed.length === 0) {
          return [];
        }
        // Silent retry round for failed/timed-out devices — most timeouts are
        // transient, and retrying here means the user never sees a warning
        // banner for a one-off blip. Progress already counted every device's
        // first-round outcome, so the retry round does not re-count.
        const stillFailed = [];
        const retryWorker = (k) =>
          fetchOne(k)
            .catch((err) => {
              if (axios.isCancel(err)) throw err;
              stillFailed.push(k);
            });
        return GlobalAccess._runServingsPool(firstRoundFailed, GlobalAccess.SERVINGS_CONCURRENCY, retryWorker)
          .then(() => stillFailed);
      }).then((stillFailed) => {
        // Double-check this is still the latest request before updating state
        if (stillFailed === undefined || currentRequestId !== GlobalAccess.servingsRequestId) {
          return;
        }

        // Clear any pending debounced update
        clearTimeout(GlobalAccess.progressUpdateTimeout);
        // Final update with complete data. servingsVersion lets consumers
        // (Reports' memo cache) detect content changes now that stale data is
        // kept during refetches and the key set alone is not a change signal.
        app.setState((prevState) => ({
          servings,
          pubkeys,
          servingsVersion: (prevState.servingsVersion || 0) + 1,
          // Guard against a stale key slipping in via the deep-link path where
          // devices hadn't resolved when `keys` was computed: only real devices
          // belong in the partial-data banner.
          servingsMissingDevices: GlobalAccess.activePubkeys(app, stillFailed),
          servingsLoadingProgress: { current: completedDevices, total: totalDevices }
        }));

        // Clear the pending request tracker
        if (GlobalAccess.pendingServingsRequest === source) {
          GlobalAccess.pendingServingsRequest = null;
        }
      });
    })
    .catch((e) => {
      // Ignore if request was cancelled
      if (axios.isCancel(e)) {
        return;
      }

      // Clear any pending debounced update
      clearTimeout(GlobalAccess.progressUpdateTimeout);
      // Auth errors are handled by axios interceptor (401/403)
      app.setState({
        isServingsLoading: false,
        servingsLoadingProgress: null,
        servingsError: e.response ? 'Unable to load servings data' : 'Network error. Please check your connection.'
      });
    })
    .finally(() => {
      // Only clear loading state if this is still the latest request
      if (currentRequestId === GlobalAccess.servingsRequestId) {
        // Clear any pending debounced update
        clearTimeout(GlobalAccess.progressUpdateTimeout);
        app.setState({
          isServingsLoading: false,
          servingsLoadingProgress: null
        });
      }
    });
  };

  /**
   * Fetch servings for devices that resolved AFTER the most recent
   * getAllServings fan-out and therefore have no data at all — the second half
   * of the "site missing from Reports until you reload" fix.
   *
   * Even with activePubkeys no longer dropping unresolved keys, a live device
   * can miss a fan-out: it may simply have timed out on the device poll that
   * ran before the report loaded, so it was absent from `devices` at fan-out
   * time and (once settled) legitimately filtered out. The next 3 s poll brings
   * it back, but nothing re-requested its servings — its site stayed blank for
   * the life of the page. This tops up those gaps in place, merging into the
   * existing servings rather than refetching the whole fleet.
   *
   * Safe to call on every render: it no-ops unless there is a completed window,
   * no fetch in flight, and at least one resolved-but-never-requested device.
   * Every key is recorded in _servingsAttempted whether or not it succeeds, so
   * a persistently failing device is tried once per window, not once per
   * render; failures surface through the existing partial-data banner.
   */
  static backfillServings = (app) => {
    const win = GlobalAccess._lastServingsWindow;
    const servings = app.state.servings || {};
    // No completed load to top up (a full fetch is coming), or one is running.
    if (!win || GlobalAccess.pendingServingsRequest || app.state.isServingsLoading) {
      return;
    }
    if (Object.keys(servings).length === 0) {
      return;
    }

    const devices = app.state.devices || {};
    const missing = Object.keys(app.state.pubkeys || {}).filter(
      (k) => devices[k] && !(k in servings) && !GlobalAccess._servingsAttempted.has(k)
    );
    if (missing.length === 0) {
      return;
    }
    missing.forEach((k) => GlobalAccess._servingsAttempted.add(k));

    const source = axios.CancelToken.source();
    GlobalAccess.pendingServingsRequest = source;
    const currentRequestId = ++GlobalAccess.servingsRequestId;

    const fetched = {};
    const failed = [];
    const worker = (k) =>
      axios({
        method: 'POST',
        url: `${Configuration.mdashURL  }/api/v2/m/device/data/servings-device?access_token=${  encodeURIComponent(k)}`,
        data: GlobalAccess.servingsBody(win.startDate, win.endDate),
        cancelToken: source.token,
        timeout: GlobalAccess.SERVINGS_TIMEOUT_MS
      })
        .then((res) => {
          fetched[k] = res.data;
        })
        .catch((err) => {
          if (axios.isCancel(err)) throw err;
          failed.push(k);
        });

    GlobalAccess._runServingsPool(missing, GlobalAccess.SERVINGS_CONCURRENCY, worker)
      .then(() => {
        if (currentRequestId !== GlobalAccess.servingsRequestId) {
          return;
        }
        app.setState((prevState) => ({
          servings: { ...(prevState.servings || {}), ...fetched },
          servingsVersion: (prevState.servingsVersion || 0) + 1,
          servingsMissingDevices: GlobalAccess.activePubkeys(app, [
            ...(prevState.servingsMissingDevices || []),
            ...failed
          ])
        }));
      })
      .catch(() => {
        // Cancelled — a newer full load superseded this backfill.
      })
      .finally(() => {
        if (GlobalAccess.pendingServingsRequest === source) {
          GlobalAccess.pendingServingsRequest = null;
        }
      });
  };

  /**
   * Refetch ONLY the devices that failed the most recent getAllServings load
   * (app.state.servingsMissingDevices) and merge their data into the existing
   * servings — used by the Reports partial-data banner's Retry button so a
   * retry doesn't redo the full fan-out. Uses the window recorded by the last
   * getAllServings call.
   */
  static retryMissingServings = (app) => {
    const win = GlobalAccess._lastServingsWindow;
    // Never retry stale (rotated-out) pubkeys — they 401 forever. See activePubkeys.
    const missing = GlobalAccess.activePubkeys(app, (app.state.servingsMissingDevices || []).slice());
    if (!win || missing.length === 0 || GlobalAccess.pendingServingsRequest) {
      return;
    }

    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    GlobalAccess.pendingServingsRequest = source;
    const currentRequestId = ++GlobalAccess.servingsRequestId;

    app.setState({
      isServingsLoading: true,
      servingsRetryAttempted: true,
      servingsLoadingProgress: { current: 0, total: missing.length }
    });

    const fetched = {};
    const stillFailed = [];
    let completed = 0;

    const worker = (k) =>
      axios({
        method: 'POST',
        url: `${Configuration.mdashURL  }/api/v2/m/device/data/servings-device?access_token=${  encodeURIComponent(k)}`,
        data: GlobalAccess.servingsBody(win.startDate, win.endDate),
        cancelToken: source.token,
        timeout: GlobalAccess.SERVINGS_TIMEOUT_MS
      })
        .then((res) => {
          fetched[k] = res.data;
        })
        .catch((err) => {
          if (axios.isCancel(err)) throw err;
          stillFailed.push(k);
        })
        .then(() => {
          completed++;
          if (currentRequestId === GlobalAccess.servingsRequestId) {
            app.setState({ servingsLoadingProgress: { current: completed, total: missing.length } });
          }
        });

    GlobalAccess._runServingsPool(missing, GlobalAccess.SERVINGS_CONCURRENCY, worker)
      .then(() => {
        if (currentRequestId !== GlobalAccess.servingsRequestId) {
          return;
        }
        app.setState((prevState) => ({
          servings: { ...(prevState.servings || {}), ...fetched },
          servingsVersion: (prevState.servingsVersion || 0) + 1,
          servingsMissingDevices: GlobalAccess.activePubkeys(app, stillFailed)
        }));
      })
      .catch(() => {
        // Cancelled — a newer full load superseded this retry.
      })
      .finally(() => {
        if (currentRequestId === GlobalAccess.servingsRequestId) {
          app.setState({
            isServingsLoading: false,
            servingsLoadingProgress: null
          });
          if (GlobalAccess.pendingServingsRequest === source) {
            GlobalAccess.pendingServingsRequest = null;
          }
        }
      });
  };

  // Cleaning-cycle alert types in the alerts table. Per the firmware enum
  // (ktpro-fw main/pulse.h) — NOT the older DB spec, which omitted 7:
  //   6 = ALERT_CLEAN_CYC_DETECT  (auto-detected cleaning)
  //   7 = ALERT_MANUAL_CLEAN_CYC  (manual cleaning)
  // Each cleaning cycle logs a start ("initiated"/"started") and a complete.
  static CLEANING_ALERT_TYPES = [6, 7];
  static ALERTS_TIMEOUT_MS = 20000;
  static alertsRequestId = 0;
  static pendingAlertsRequest = null;

  /**
   * Fetch the cleaning-cycle event log (alerts table, types 6 & 7) for every
   * active device over [startDate, endDate] and store it in
   * app.state.cleaningAlerts, keyed by pubkey. Mirrors getAllServings' per-
   * device fan-out (the alerts-device ACL is the exact parallel of servings-
   * device), but is single-round best-effort: a device that fails to load just
   * has no log.
   *
   * Rows are stored raw (positional arrays as the data endpoint returns them);
   * cleaningModel parses and pairs them. Only cleaning-type rows are kept to
   * bound memory.
   *
   * Returns the pubkeys requested ([] when none is known yet, undefined without
   * a session) so the caller can tell when a device that resolved later is
   * missing from the history. On failure cleaningAlertsError holds a
   * translation key, never display text.
   */
  static getAllCleaningAlerts = (app, startDate, endDate) => {
    const token = getToken();
    if (GlobalAccess._blockedBySessionChange(app, token)) return;
    if (!token) return;

    if (GlobalAccess.pendingAlertsRequest) {
      GlobalAccess.pendingAlertsRequest.cancel('New alerts request started');
    }
    const source = axios.CancelToken.source();
    GlobalAccess.pendingAlertsRequest = source;
    const currentRequestId = ++GlobalAccess.alertsRequestId;

    app.setState({ isCleaningAlertsLoading: true, cleaningAlertsError: null });

    const statePubkeys = app.state.pubkeys || {};
    const keys = GlobalAccess.activePubkeys(app, Object.keys(statePubkeys));
    if (keys.length === 0) {
      app.setState({ isCleaningAlertsLoading: false, cleaningAlerts: {} });
      if (GlobalAccess.pendingAlertsRequest === source) GlobalAccess.pendingAlertsRequest = null;
      return keys;
    }

    const alerts = {};
    const rowType = (row) => (Array.isArray(row) ? row[5] : row?.type);

    const fetchOne = (k) =>
      axios({
        method: 'POST',
        url: `${Configuration.mdashURL}/api/v2/m/device/data/alerts-device?access_token=${encodeURIComponent(k)}`,
        data: { params: [startDate, endDate] },
        cancelToken: source.token,
        timeout: GlobalAccess.ALERTS_TIMEOUT_MS
      })
        .then((res) => {
          const rows = (res.data && res.data.rows) || [];
          alerts[k] = rows.filter((r) => GlobalAccess.CLEANING_ALERT_TYPES.includes(Number(rowType(r))));
        })
        .catch((err) => {
          if (axios.isCancel(err)) throw err;
          // Best-effort: a failed device simply has no cleaning log this load.
        });

    GlobalAccess._runServingsPool(keys, GlobalAccess.SERVINGS_CONCURRENCY, fetchOne)
      .then(() => {
        if (currentRequestId !== GlobalAccess.alertsRequestId) return;
        app.setState((prevState) => ({
          cleaningAlerts: alerts,
          cleaningAlertsVersion: (prevState.cleaningAlertsVersion || 0) + 1
        }));
      })
      .catch((e) => {
        if (axios.isCancel(e)) return;
        if (currentRequestId === GlobalAccess.alertsRequestId) {
          app.setState({ cleaningAlertsError: AppConstant.CLEANING_HISTORY_LOAD_ERROR });
        }
      })
      .finally(() => {
        if (currentRequestId === GlobalAccess.alertsRequestId) {
          app.setState({ isCleaningAlertsLoading: false });
          if (GlobalAccess.pendingAlertsRequest === source) GlobalAccess.pendingAlertsRequest = null;
        }
      });
    return keys;
  };

}
